import React from 'react';
import { View } from 'react-native';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Reveal from '@/components/Landing/Reveal';
import { Section, useLanding } from '@/components/Landing/LandingKit';
import { ACCENT, fluid } from '@/components/Landing/landingTokens';
import { Band, SectionIntro } from '@/components/Landing/MarketingKit';
import { INDUSTRIES } from '@/constants/enterpriseContent';

/**
 * Eight industries, set as a hairline matrix rather than eight cards.
 *
 * This is reference material — a reader looks for their own sector and ignores
 * the rest — so it wants to be scannable, not eight boxes competing for the
 * same attention.
 *
 * `edges="top"` leaves the band open at the bottom so it runs into the closing
 * band beneath it, and the page settles into its ending rather than stepping.
 */
function EnterpriseIndustries() {
  const { band, isCompact, width } = useLanding();

  const columns = isCompact ? 1 : width > 1080 ? 4 : 2;
  const pad = width > 900 ? 28 : 22;

  return (
    <Band glow="86% 26%" edges="top">
      <Section label="Enterprise AI use cases by industry">
        <SectionIntro
          tone="band"
          kicker="Use cases"
          lines={[['Every industry has its'], ['own hard problem.']]}
          lede="A starting point rather than a menu. If your sector is not listed, the work still looks much the same."
          ledeMax={560}
        />

        <Reveal delay={100}>
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginTop: fluid(width, 36, 54),
              borderTopWidth: 1,
              borderColor: band.rule,
            }}
          >
            {INDUSTRIES.map((item, index) => {
              const firstInRow = index % columns === 0;

              return (
                <View
                  key={item.industry}
                  style={{
                    flexBasis: `${100 / columns}%`,
                    maxWidth: `${100 / columns}%`,
                    minWidth: 0,
                    paddingVertical: pad,
                    paddingRight: isCompact ? 0 : pad,
                    paddingLeft: firstInRow ? 0 : pad,
                    borderLeftWidth: firstInRow ? 0 : 1,
                    borderBottomWidth: 1,
                    borderColor: band.rule,
                    gap: 14,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <Icon name={item.icon} size={13} color={ACCENT} solid />
                    <Text
                      weight="bold"
                      style={{ color: band.ink, fontSize: 15, letterSpacing: -0.22, flex: 1, minWidth: 0 }}
                    >
                      {item.industry}
                    </Text>
                  </View>

                  <View style={{ gap: 7 }}>
                    {item.examples.map((example) => (
                      <Text
                        key={example}
                        style={{ color: band.muted, fontSize: 13.5, lineHeight: 20 }}
                      >
                        {example}
                      </Text>
                    ))}
                  </View>
                </View>
              );
            })}
          </View>
        </Reveal>
      </Section>
    </Band>
  );
}

export default React.memo(EnterpriseIndustries);
